import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable, isDevMode, PLATFORM_ID } from "@angular/core";
import { CookiesService } from "../cookies/cookies.service";
import { isPlatformServer } from "@angular/common";
import { Observable } from "rxjs";

/** Backend used by SSR in a production build when CBOX_API_URL is unset. */
const DEFAULT_SSR_BACKEND_URL = "http://10.10.0.141:3040";
/** Backend used by SSR in a dev build when CBOX_API_URL is unset. */
const DEFAULT_SSR_DEV_BACKEND_URL = "http://10.10.0.122:3040";

/**
 * A request for the app's own static files (the `assets/` SVG icons), which
 * SSR leaves alone: platform-server resolves it against the render URL.
 * Only a relative `assets/…` path counts. The check used to be
 * `url.includes("assets")`, which let any URL holding that word (an
 * absolute one included) skip the backend rewrite (#3596).
 */
export function isSsrAssetRequest(url: string): boolean {
  return /^\/?assets\//.test(url) && !url.includes("..") &&
    !url.includes("\\");
}

/**
 * Where SSR sends a request: a `backend…` URL is rewritten to the backend
 * base URL. Anything else is not a backend call, and gets null: SSR then
 * sends it as it is, without the visitor's session cookie (#3596).
 */
export function ssrBackendUrl(url: string, backend: string): string | null {
  const rewritten = url
    .replace(/^http.+?backend/g, backend)
    .replace(/^backend/g, backend);

  return rewritten.startsWith(backend) ? rewritten : null;
}

/**
 * Reads the backend base URL from the CBOX_API_URL environment variable.
 * Only ever called from a server-platform branch: `typeof process` keeps it
 * inert (no ReferenceError, no bundler shim) if it ever runs in a browser.
 * Returns null when the variable is unset or empty.
 */
function readBackendUrlFromEnv(): string | null {
  if (typeof process === "undefined") {
    return null;
  }
  const value = process.env["CBOX_API_URL"];
  if (!value) {
    return null;
  }
  const trimmed = value.trim().replace(/\/+$/, "");

  return trimmed === "" ? null : trimmed;
}

@Injectable({ providedIn: 'root' })
export class CredentialsInterceptor implements HttpInterceptor {

  private readonly cookiesService = inject(CookiesService);
  private readonly platformId = inject(PLATFORM_ID);

  private url = DEFAULT_SSR_BACKEND_URL;
  /**
   * True when CBOX_API_URL named the backend explicitly (per-instance dev
   * stacks, e2e). Such a target is rewritten to even in a dev build, where
   * the hard-coded defaults are left alone for backwards compatibility.
   */
  private urlFromEnv = false;

  constructor() {
    if (isPlatformServer(this.platformId)) {
      const fromEnv = readBackendUrlFromEnv();
      if (fromEnv !== null) {
        this.url = fromEnv;
        this.urlFromEnv = true;
      } else if (isDevMode()) {
        this.url = DEFAULT_SSR_DEV_BACKEND_URL;
      }
    }
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let newReq: HttpRequest<any>;
    if (isPlatformServer(this.platformId) && (this.urlFromEnv || !isDevMode())) {
      if (isSsrAssetRequest(req.url)) {
        return next.handle(req);
      }
      const url = ssrBackendUrl(req.url, this.url);
      if (url === null) {
        return next.handle(req);
      }
      const cookies = this.cookiesService.get();
      newReq = req.clone({
        url,
        withCredentials: true,
        setHeaders: {
          Cookie: cookies,
        },
      });
    } else {
      newReq = req.clone({
        withCredentials: true,
      });
    }
    return next.handle(newReq);
  }

}
